import consola from 'consola'
import { ref } from 'vue'

/**
 * История событий разлогинивания
 */
interface LogoutEvent {
  timestamp: string
  reason: string
  source: string
  metadata?: Record<string, any>
}

const logoutHistory = ref<LogoutEvent[]>([])
const MAX_HISTORY_SIZE = 50

/**
 * Composable для отслеживания всех событий разлогинивания
 * Полезно для отладки проблем с разлогиниванием
 */
export const useLogoutEventTracker = () => {
  /**
   * Добавляет событие в историю
   */
  const trackLogoutEvent = (event: Omit<LogoutEvent, 'timestamp'>) => {
    const logoutEvent: LogoutEvent = {
      ...event,
      timestamp: new Date().toISOString(),
    }

    logoutHistory.value.unshift(logoutEvent)

    // Ограничиваем размер истории
    if (logoutHistory.value.length > MAX_HISTORY_SIZE) {
      logoutHistory.value = logoutHistory.value.slice(0, MAX_HISTORY_SIZE)
    }

    // Сохраняем в localStorage для отладки
    if (process.client) {
      try {
        localStorage.setItem('logout_history', JSON.stringify(logoutHistory.value.slice(0, 10)))
      } catch {
        consola.warn('[LogoutEventTracker] Не удалось сохранить историю в localStorage')
      }
    }

    consola.debug('[LogoutEventTracker] 📝 Новое событие разлогинивания:', logoutEvent)
  }

  /**
   * Получает историю событий
   */
  const getLogoutHistory = () => {
    return logoutHistory.value
  }

  /**
   * Очищает историю
   */
  const clearLogoutHistory = () => {
    logoutHistory.value = []

    if (process.client) {
      localStorage.removeItem('logout_history')
    }

    consola.debug('[LogoutEventTracker] 🧹 История событий очищена')
  }

  /**
   * Восстанавливает историю из localStorage
   */
  const restoreLogoutHistory = () => {
    /* v8 ignore next */
    if (!process.client) return

    try {
      const stored = localStorage.getItem('logout_history')
      if (stored) {
        logoutHistory.value = JSON.parse(stored)
        consola.debug('[LogoutEventTracker] 📥 История восстановлена из localStorage:', logoutHistory.value.length, 'событий')
      }
    } catch {
      consola.warn('[LogoutEventTracker] Не удалось восстановить историю из localStorage')
    }
  }

  /**
   * Выводит историю в консоль в удобном формате
   */
  const printLogoutHistory = () => {
    if (logoutHistory.value.length === 0) {
      consola.debug('[LogoutEventTracker] 📋 История событий пуста')
      return
    }

    consola.info(`[LogoutEventTracker] 📋 История событий разлогинивания (${logoutHistory.value.length})`)
    for (const [index, event] of logoutHistory.value.entries()) {
      consola.debug(`${index + 1}. ${event.timestamp}`)
      consola.debug('   Причина:', event.reason)
      consola.debug('   Источник:', event.source)
      if (event.metadata) {
        consola.debug('   Метаданные:', event.metadata)
      }
      consola.debug('---')
    }
  }

  // Восстанавливаем историю при первом использовании
  if (process.client && logoutHistory.value.length === 0) {
    restoreLogoutHistory()
  }

  // Экспортируем в window для отладки
  if (process.client) {
    ;(window as any).$logoutHistory = {
      get: getLogoutHistory,
      print: printLogoutHistory,
      clear: clearLogoutHistory,
    }
  }

  return {
    trackLogoutEvent,
    getLogoutHistory,
    clearLogoutHistory,
    printLogoutHistory,
  }
}
